import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

interface Install {
  edition: 'cloud' | 'onprem';
  installId: string | null;
}

/** Установка у заказчика (ЛЦ-07): код установки и активация ключа. В облачном издании раздел не показывается. */
export function InstallSection() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'license', 'install'],
    queryFn: () => api<Install>('/admin/license/install'),
  });
  const [key, setKey] = useState('');
  const activate = useMutation({
    mutationFn: () =>
      api<{ planName: string; endsAt: string | null }>('/admin/license/activate', {
        method: 'POST',
        body: { key: key.trim() },
      }),
    onSuccess: (r) => {
      toast(`Ключ принят: тариф «${r.planName}», ${r.endsAt ? `до ${date(r.endsAt)}` : 'бессрочно'}`);
      setKey('');
      qc.invalidateQueries({ queryKey: ['admin', 'license'] });
    },
  });
  if (q.data?.edition !== 'onprem') return null;
  const id = q.data.installId;
  const copy = async () => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      toast('Код скопирован');
    } catch {
      toast('Не удалось скопировать — выделите код и скопируйте вручную', 'danger');
    }
  };
  return (
    <Panel title="Установка на ваших серверах" meta="ЛЦ-07">
      <div className="flex flex-col gap-4 text-[14px]">
        <div>
          <div className="mb-1.5 text-[13px] text-ink-2">Код установки</div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="num min-w-0 flex-1 rounded-md bg-surface-2 px-3 py-2.5 text-[14px] break-all text-ink">
              {id ?? '—'}
            </code>
            <Button onClick={copy} disabled={!id}>
              Скопировать
            </Button>
          </div>
          <p className="mt-2 text-[13px] text-ink-2">
            Сообщите этот код владельцу портала — он выпустит ключ, который работает только на вашей
            установке.
          </p>
        </div>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            activate.mutate();
          }}
        >
          <Field
            label="Ключ лицензии"
            htmlFor="license-key"
            hint="Строка начинается с EPC1. Вставьте её целиком."
          >
            <Textarea
              id="license-key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              rows={4}
              spellCheck={false}
              autoComplete="off"
              className="font-mono break-all"
            />
          </Field>
          {activate.isError ? <ErrorBox error={activate.error} /> : null}
          <div>
            <Button
              type="submit"
              variant="primary"
              loading={activate.isPending}
              disabled={key.trim().length < 40}
            >
              Активировать ключ
            </Button>
          </div>
        </form>
      </div>
    </Panel>
  );
}
